import {
    type ActionTriggerConfig,
    type FilterTriggerConfig,
    type ScheduleTriggerConfig,
    type TriggerConfig
} from '@spike/automation-model';

export type TemplateCategory = 'onboarding' | 'reminders' | 'retention' | 'reputation' | 'loyalty' | 'compliance';

export interface TemplateStep {
    type: 'delay' | 'sms' | 'email';
    amount?: number;
    unit?: 'minutes' | 'hours' | 'days' | 'weeks';
    message?: string;
    subject?: string;
    body?: string;
}

export interface AutomationTemplate {
    id: string;
    name: string;
    description: string;
    category: TemplateCategory;
    serviceScope: string;
    trigger: TriggerConfig;
    steps: TemplateStep[];
}

export const TEMPLATE_CATEGORIES: Array<{ id: TemplateCategory; label: string }> = [
    { id: 'onboarding', label: 'Welcome & Onboarding' },
    { id: 'reminders', label: 'Appointment Reminders' },
    { id: 'retention', label: 'Retention & Rebooking' },
    { id: 'reputation', label: 'Post-Service & Reputation' },
    { id: 'loyalty', label: 'Loyalty & Special Dates' },
    { id: 'compliance', label: 'Health & Compliance' }
];

const action = (
    eventType: string,
    subjectConditions: ActionTriggerConfig['subjectConditions'] = []
): ActionTriggerConfig => ({
    kind: 'action',
    eventType,
    eventConditions: [],
    subjectConditions
});

const schedule = (
    anchor: string,
    offset: ScheduleTriggerConfig['offset'],
    extra: Partial<ScheduleTriggerConfig> = {}
): ScheduleTriggerConfig => ({
    kind: 'schedule',
    anchor,
    offset,
    recurrence: 'once',
    sendAt: null,
    conditions: [],
    subjectConditions: [],
    ...extra
});

const filter = (
    frequency: FilterTriggerConfig['frequency'],
    criteria: FilterTriggerConfig['criteria'] = []
): FilterTriggerConfig => ({
    kind: 'filter',
    frequency,
    time: '09:00',
    criteria
});

const criterion = (field: string, operator: any, value: any) => ({
    id: `tpl-${field}`,
    field,
    operator,
    value
});

export const AUTOMATION_TEMPLATES: AutomationTemplate[] = [
    {
        id: 'new-client-welcome',
        name: 'New Client Welcome',
        description: 'Auto-message when a new client is added, with a link to complete their profile.',
        category: 'onboarding',
        serviceScope: 'All Services',
        trigger: action('client_converted'),
        steps: [
            {
                type: 'sms',
                message:
                    'Welcome to [Business Name], [Client Name]! Finish setting up your profile here: [Intake Form Link]'
            }
        ]
    },
    {
        id: 'intake-form-request',
        name: 'Intake Form Request',
        description: 'Sends the intake form before the first appointment.',
        category: 'onboarding',
        serviceScope: 'All Services',
        trigger: {
            ...action('appointment_booked'),
            eventConditions: [criterion('total_completed_bookings', 'eq', 0)]
        },
        steps: [
            {
                type: 'sms',
                message:
                    "Hi [Client Name], we can't wait to see you on [Appointment Date]! Please complete your intake form first: [Intake Form Link]"
            }
        ]
    },
    {
        id: 'vaccine-record-request',
        name: 'Vaccine Record Request',
        description: 'Asks for vaccine records when a new pet profile is created.',
        category: 'onboarding',
        serviceScope: 'All Services',
        trigger: action('pet_added_to_client'),
        steps: [
            {
                type: 'sms',
                message:
                    "Thanks for adding [Pet Name]! Please send over their current vaccine records so we're ready for their first visit."
            }
        ]
    },

    {
        id: 'reminder-24h',
        name: '24h Reminder',
        description: 'SMS or email the day before with date, time, and location.',
        category: 'reminders',
        serviceScope: 'All Services',
        trigger: schedule('appointment_starts_at', { direction: 'before', amount: 24, unit: 'hours' }),
        steps: [
            {
                type: 'sms',
                message:
                    'Reminder: [Pet Names] has an appointment at [Business Name] on [Appointment Date] at [Appointment Time]. [Business Address]'
            }
        ]
    },
    {
        id: 'booking-confirmation',
        name: 'Booking Confirmation',
        description: 'Instant message when an appointment is accepted or created.',
        category: 'reminders',
        serviceScope: 'All Services',
        trigger: action('appointment_booked'),
        steps: [
            {
                type: 'sms',
                message:
                    "You're booked! [Pet Names] — [Service Name] on [Appointment Date] at [Appointment Time]. See you at [Business Address]."
            }
        ]
    },
    {
        id: 'pre-check-in-instructions',
        name: 'Pre Check-In Instructions',
        description: 'Reminds arrival time, what to bring, and facility rules.',
        category: 'reminders',
        serviceScope: 'Boarding',
        trigger: schedule(
            'appointment_starts_at',
            { direction: 'before', amount: 2, unit: 'days' },
            {
                sendAt: '10:00',
                subjectConditions: [criterion('service_category', 'in', ['boarding'])]
            }
        ),
        steps: [
            {
                type: 'sms',
                message:
                    "[Pet Names]'s stay starts [Appointment Date] at [Appointment Time]. Please bring their food, medications and vaccine records. Questions? Call us at [Business Phone]."
            }
        ]
    },
    {
        id: 'daily-daycare-reminder',
        name: 'Daily Daycare Reminder',
        description: 'Evening notice to confirm next-day attendance.',
        category: 'reminders',
        serviceScope: 'Daycare',
        trigger: schedule(
            'appointment_starts_at',
            { direction: 'before', amount: 1, unit: 'days' },
            {
                sendAt: '18:00',
                subjectConditions: [criterion('service_category', 'in', ['daycare'])]
            }
        ),
        steps: [
            {
                type: 'sms',
                message:
                    'See you tomorrow! [Pet Names] is booked for daycare on [Appointment Date]. Reply if anything changes.'
            }
        ]
    },

    {
        id: 'rebook-reminder',
        name: 'Rebook Reminder',
        description: 'Nudges the client to rebook when their next grooming session is approaching.',
        category: 'retention',
        serviceScope: 'Grooming',
        trigger: schedule(
            'last_appointment_at',
            { direction: 'after', amount: 6, unit: 'weeks' },
            {
                sendAt: '10:00',
                conditions: [criterion('has_upcoming_appointment', 'is', 'false')],
                subjectConditions: [criterion('service_category', 'in', ['grooming'])]
            }
        ),
        steps: [
            {
                type: 'sms',
                message:
                    "Hi [Client Name] — it's been about 6 weeks since [Pet Names] last visit. Ready to book the next one? [Booking Link]"
            }
        ]
    },
    {
        id: 'lapsed-client-reactivation',
        name: 'Lapsed Client Reactivation',
        description: 'Message to clients with no appointments in 60+ days.',
        category: 'retention',
        serviceScope: 'All Services',
        trigger: filter('monthly', [
            criterion('last_appointment_at', 'days_ago_gt', 60),
            criterion('has_upcoming_appointment', 'is', 'false')
        ]),
        steps: [
            {
                type: 'sms',
                message: "We miss you, [Client Name]! It's been a while — book your next visit here: [Booking Link]"
            }
        ]
    },
    {
        id: 'abandoned-booking-recovery',
        name: 'Abandoned Booking Recovery',
        description: "Follows up with clients who started but didn't complete their online booking.",
        category: 'retention',
        serviceScope: 'All Services',
        trigger: schedule('booking_attempt_abandoned_at', { direction: 'after', amount: 2, unit: 'hours' }),
        steps: [
            {
                type: 'sms',
                message:
                    "Looks like you didn't finish booking with [Business Name]. Pick up where you left off: [Booking Link]"
            }
        ]
    },
    {
        id: 'irregular-client-reminder',
        name: 'Irregular Client Reminder',
        description: "For clients who haven't visited in 2+ weeks with no upcoming appointment.",
        category: 'retention',
        serviceScope: 'Daycare',
        trigger: filter('weekly', [
            criterion('last_appointment_at', 'days_ago_gt', 14),
            criterion('has_upcoming_appointment', 'is', 'false'),
            criterion('service_category', 'in', ['daycare'])
        ]),
        steps: [
            {
                type: 'sms',
                message:
                    'Hi [Client Name]! Daycare spots are open this week if [Pet Names] wants to come play: [Booking Link]'
            }
        ]
    },

    {
        id: 'review-request',
        name: 'Review Request',
        description: 'Asks for a Google or Yelp review right after the service is completed.',
        category: 'reputation',
        serviceScope: 'All Services',
        trigger: action('appointment_completed'),
        steps: [
            { type: 'delay', amount: 2, unit: 'hours' },
            {
                type: 'sms',
                message: 'Thanks for visiting [Business Name]! How did we do with [Pet Names]? [Review Link]'
            }
        ]
    },
    {
        id: 'grooming-report-delivery',
        name: 'Grooming Report Delivery',
        description: 'Shares grooming notes when the appointment ends.',
        category: 'reputation',
        serviceScope: 'Grooming',
        trigger: action('appointment_completed', [criterion('service_category', 'in', ['grooming'])]),
        steps: [
            {
                type: 'sms',
                message: '[Pet Names] is all done and looking great! Your groomer was [Staff Name]. See you next time!'
            }
        ]
    },
    {
        id: 'ready-for-pick-up',
        name: 'Ready For Pick-Up',
        description: "Notifies the owner when their pet's stay is complete.",
        category: 'reputation',
        serviceScope: 'Boarding',
        trigger: action('appointment_completed', [criterion('service_category', 'in', ['boarding'])]),
        steps: [
            {
                type: 'sms',
                message: '[Pet Names] is ready to go home! Come pick them up at [Business Address].'
            }
        ]
    },

    {
        id: 'pet-birthday',
        name: 'Pet Birthday',
        description: "Special greeting on the pet's birthday, optionally with a discount.",
        category: 'loyalty',
        serviceScope: 'All Services',
        trigger: schedule(
            'pet_birthday',
            { direction: 'before', amount: 0, unit: 'days' },
            { recurrence: 'annual', sendAt: '09:00' }
        ),
        steps: [
            {
                type: 'sms',
                message: 'Happy birthday [Pet Name]! 🎉 From all of us at [Business Name].'
            }
        ]
    },
    {
        id: 'client-anniversary',
        name: 'Client Anniversary',
        description: 'Message on the 1-year mark since their first visit.',
        category: 'loyalty',
        serviceScope: 'All Services',
        trigger: schedule(
            'client_anniversary',
            { direction: 'before', amount: 0, unit: 'days' },
            { recurrence: 'annual', sendAt: '10:00' }
        ),
        steps: [
            {
                type: 'sms',
                message:
                    "[Client Name], it's been a year since your first visit to [Business Name]. Thank you for trusting us!"
            }
        ]
    },
    {
        id: 'membership-welcome',
        name: 'Membership Welcome',
        description: 'Confirms enrollment, perks, and billing cycle when a client joins a membership plan.',
        category: 'loyalty',
        serviceScope: 'All Services',
        trigger: action('membership_started'),
        steps: [
            {
                type: 'sms',
                message:
                    'Welcome to [Membership Name], [Client Name]! You have [Credits Remaining] credits to use. Book anytime: [Booking Link]'
            }
        ]
    },
    {
        id: 'membership-renewal-reminder',
        name: 'Membership Renewal Reminder',
        description: 'Alerts the client before their plan auto-renews.',
        category: 'loyalty',
        serviceScope: 'All Services',
        trigger: schedule(
            'membership_renews_at',
            { direction: 'before', amount: 5, unit: 'days' },
            { sendAt: '10:00' }
        ),
        steps: [
            {
                type: 'sms',
                message: 'Heads up [Client Name] — your [Membership Name] renews on [Renewal Date].'
            }
        ]
    },
    {
        id: 'package-credits-running-low',
        name: 'Package Credits Running Low',
        description: 'Notifies when a prepaid package is almost used up.',
        category: 'loyalty',
        serviceScope: 'All Services',
        trigger: action('enrollment_credits_low', [criterion('remaining_credits', 'lt', 2)]),
        steps: [
            {
                type: 'sms',
                message:
                    '[Client Name], you have [Credits Remaining] credits left on [Membership Name]. Want to top up? [Booking Link]'
            }
        ]
    },

    {
        id: 'upcoming-vaccine-expiry',
        name: 'Upcoming Vaccine Expiry',
        description: 'Alerts the owner 30 days before a required vaccine expires.',
        category: 'compliance',
        serviceScope: 'All Services',
        trigger: schedule('vaccine_expires_at', { direction: 'before', amount: 30, unit: 'days' }, { sendAt: '10:00' }),
        steps: [
            {
                type: 'sms',
                message:
                    "[Pet Name]'s [Vaccine Name] expires on [Vaccine Expiry Date]. Please send us an updated record before their next visit."
            }
        ]
    },
    {
        id: 'expired-vaccine',
        name: 'Expired Vaccine',
        description: 'Requests an updated record when a vaccine has already expired.',
        category: 'compliance',
        serviceScope: 'All Services',
        trigger: schedule('vaccine_expires_at', { direction: 'after', amount: 1, unit: 'days' }, { sendAt: '10:00' }),
        steps: [
            {
                type: 'sms',
                message:
                    "[Pet Name]'s [Vaccine Name] expired on [Vaccine Expiry Date]. We'll need an updated record before we can see them."
            }
        ]
    },
    {
        id: 'unsigned-waiver-reminder',
        name: 'Unsigned Waiver Reminder',
        description: "Sends the service agreement if it hasn't been signed yet.",
        category: 'compliance',
        serviceScope: 'Boarding',
        trigger: schedule(
            'appointment_starts_at',
            { direction: 'before', amount: 3, unit: 'days' },
            {
                sendAt: '10:00',
                conditions: [criterion('has_unsigned_mandatory_agreement', 'is', 'true')],
                subjectConditions: [criterion('service_category', 'in', ['boarding'])]
            }
        ),
        steps: [
            {
                type: 'sms',
                message:
                    "Hi [Client Name] — we still need your signed service agreement before [Pet Names]'s stay on [Appointment Date]. Please sign at check-in or give us a call."
            }
        ]
    }
];

export const templatesByCategory = (): Array<{
    category: TemplateCategory;
    label: string;
    templates: AutomationTemplate[];
}> =>
    TEMPLATE_CATEGORIES.map(({ id, label }) => ({
        category: id,
        label,
        templates: AUTOMATION_TEMPLATES.filter(template => template.category === id)
    })).filter(group => group.templates.length > 0);
